import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/usePdfAsImage'
import { useCantileverPageHostsOptional } from 'features/knowledgeDocumentViewer/contexts/CantileverPageHostsContext'
import { resolvePageImageFileRefDual } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useCallback, useMemo } from 'react'

import { PageImage } from './continuousScrollStyles'

const CONTINUOUS_PDF_SCALE = 4

interface ContinuousPageImageProps {
  documentId: string
  pageNumber: number
  pdfUrl: string | undefined
  totalPages: number
  onDimensionsKnown?: (
    pageNumber: number,
    width: number,
    height: number
  ) => void
}

export const ContinuousPageImage = React.memo(function ContinuousPageImage({
  documentId,
  pageNumber,
  pdfUrl,
  totalPages,
  onDimensionsKnown,
}: ContinuousPageImageProps) {
  const pageHosts = useCantileverPageHostsOptional()
  const pageHostId = pageHosts?.getCantileverPageHostId(pageNumber)
  const hiResQuery = useMemo(
    () =>
      resolvePageImageFileRefDual(documentId, pageNumber, 'hiRes', pageHostId),
    [documentId, pageHostId, pageNumber]
  )
  const { url: hiResUrl, isLoading: isHiResLoading } =
    useRolodexSignedURLs(hiResQuery)

  const pdfFallbackEnabled = !hiResUrl && !isHiResLoading
  const { imageUrl: pdfImageUrl } = usePdfAsImage(
    pdfFallbackEnabled ? pdfUrl : undefined,
    pageNumber,
    CONTINUOUS_PDF_SCALE,
    totalPages,
    pdfFallbackEnabled
  )

  const displayUrl = hiResUrl ?? pdfImageUrl

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      onDimensionsKnown?.(
        pageNumber,
        e.currentTarget.naturalWidth,
        e.currentTarget.naturalHeight
      )
    },
    [onDimensionsKnown, pageNumber]
  )

  if (!displayUrl) return null

  return (
    <PageImage
      src={displayUrl}
      alt={`Page ${pageNumber}`}
      onLoad={handleLoad}
      draggable={false}
    />
  )
})
